"use strict";
/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.UnknownComponentError = exports.InvalidParamTypeError = exports.InvalidComponentSpecificationError = void 0;
exports.getSchemaFromParameterDefinition = getSchemaFromParameterDefinition;
exports.getParamsSchemaForComponent = getParamsSchemaForComponent;
exports.getComponentSchema = getComponentSchema;
exports.getSpecSchema = getSpecSchema;
const joi_1 = __importDefault(require("joi"));
const error_values_1 = require("@karya/error-values");
const Index_1 = require("../params/input/Index");
const Index_2 = require("../params/output/Index");
const Index_3 = require("../components/Index");
const ParameterDefinitions_1 = require("./ParameterDefinitions");
/**
 * An error that is thrown when a Generic Task component does not conform to the
 * specification format.
 */
class InvalidComponentSpecificationError extends error_values_1.CodedError {
    constructor(component, reason) {
        super('InvalidComponentSpecificationError', "Component doesn't conform to the Generic Task Specification format", {
            component,
            reason,
        });
    }
}
exports.InvalidComponentSpecificationError = InvalidComponentSpecificationError;
/**
 * An error that is thrown when an unknown/invalid parameter type is encountered
 * while building a component schema.
 * @see {@link inputParamSchema} and {@link outputParamSchema} for allowed parameter types.
 */
class InvalidParamTypeError extends error_values_1.CodedError {
    constructor(details) {
        const allowedParamTypes = details.kind === 'INPUT_PARAM' ? Object.keys(Index_1.inputParamSchema) : Object.keys(Index_2.outputParamSchema);
        const message = `'${details.paramType}' is not a valid param type. Valid types include ${allowedParamTypes.join(', ')}`;
        super('InvalidParamTypeError', message, details);
    }
}
exports.InvalidParamTypeError = InvalidParamTypeError;
/**
 * An error that is thrown when an unknown component is encountered in the generic
 * task specification. A component is considered unknown if there is no corresponding
 * definition for its ctype, dtype, function_id or loop_id.
 */
class UnknownComponentError extends error_values_1.CodedError {
    constructor(component) {
        super('UnknownComponentError', 'Unknown component found - \
      The provided component is either invalid, unsupported or has a typo in ctype, dtype, function_id or loop_id', { component });
    }
}
exports.UnknownComponentError = UnknownComponentError;
/**
 * Base Joi schema that validates the set of properties available in all task components.
 */
const baseComponentSchema = joi_1.default.object({
    name: joi_1.default.string(),
    description: joi_1.default.string(),
    key: joi_1.default.string().required(),
    ctype: joi_1.default.string()
        .valid(...Index_3.ctypeList)
        .required(),
    next: Index_1.referenceInputParamSchema.required(),
});
/**
 * Retrieves the appropriate Joi validation schema for a given parameter definition.
 *
 * @param param The parameter definition object.
 * @returns A {@link Result} object containing the Joi schema on success, or an {@link InvalidParamTypeError} on failure.
 */
function getSchemaFromParameterDefinition(
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- The typing needs to be improved in this case
param) {
    if (param.type == 'output') {
        const paramType = param.paramType;
        if (!(paramType in Index_2.outputParamSchema)) {
            return (0, error_values_1.err)(new InvalidParamTypeError({ paramType, kind: 'OUTPUT_PARAM' }));
        }
        return (0, error_values_1.ok)(Index_2.outputParamSchema[paramType]);
    }
    else {
        const paramType = param.paramType;
        if (!(paramType in Index_1.inputParamSchema)) {
            return (0, error_values_1.err)(new InvalidParamTypeError({ paramType, kind: 'INPUT_PARAM' }));
        }
        return (0, error_values_1.ok)(Index_1.inputParamSchema[paramType]);
    }
}
/**
 * Builds a Joi schema to validate a component's parameters based on its definitions.
 *
 * @template Component - The type of the component for which the schema is being built.
 * @param parameterDefinitions - An array of parameter definitions for the component.
 * @returns A {@link Result} which is an `Ok` with the Joi object schema for the component's parameters,
 * or an `Err` with an {@link InvalidParamTypeError}.
 * @see {@link GenericInputParam} and {@link GenericOutputParam} for details on how the schema validation works.
 */
function getParamsSchemaForComponent(parameterDefinitions) {
    const base = {};
    for (const paramDef of parameterDefinitions) {
        const [schema, error] = getSchemaFromParameterDefinition(paramDef).toTuple();
        if (error) {
            error.addContext(`When fetching schema for ${paramDef.id}`);
            return (0, error_values_1.err)(error);
        }
        base[paramDef.id] = paramDef.required ? schema.required() : schema;
    }
    const componentParamsSchema = joi_1.default.object(base).unknown(true);
    return (0, error_values_1.ok)(componentParamsSchema);
}
/**
 * Generates a complete Joi validation schema for a single generic task component.
 *
 * @template Component - The type of the component.
 * @param component - The component object for which to generate the schema.
 * @param options - Configuration options for schema generation.
 * @returns A {@link Result} which is an `Ok` with the full Joi schema for the component, or an `Err`
 * with either an {@link UnknownComponentError} or an {@link InvalidParamTypeError}.
 */
function getComponentSchema(component, options) {
    var _a;
    const parameterDefinitions = (_a = (0, ParameterDefinitions_1.getComponentParameters)(component)) !== null && _a !== void 0 ? _a : [];
    if ((options === null || options === void 0 ? void 0 : options.failForUnknownComponents) && parameterDefinitions.length === 0) {
        return (0, error_values_1.err)(new UnknownComponentError(component));
    }
    const [parametersSchema, error] = getParamsSchemaForComponent(parameterDefinitions).toTuple();
    if (error) {
        error.addContext('When building schema using component parameter definitions');
        return (0, error_values_1.err)(error);
    }
    const componentSchema = baseComponentSchema.concat(parametersSchema);
    return (0, error_values_1.ok)(componentSchema);
}
/**
 * Generates a Joi validation schema for an entire generic task specification.
 * It iterates through each component in the specification and builds a schema for it.
 *
 * @param spec - The task specification object (validated at runtime).
 * @returns A {@link Result} which is an `Ok` with a Joi schema for the full specification,
 * or an `Err` if any component fails schema generation.
 */
function getSpecSchema(spec, options) {
    const base = {
        start: joi_1.default.object().required(),
        end: joi_1.default.object().required(),
    };
    const specSchema = joi_1.default.object().unknown(true);
    const { error, value } = specSchema.validate(spec);
    if (error) {
        return (0, error_values_1.ok)(joi_1.default.object(base).unknown(true));
    }
    // Joi validates value is an object; we assume components have the right shape
    const specObj = value;
    for (const componentKey in specObj) {
        const component = specObj[componentKey];
        const [componentSchema, error] = getComponentSchema(component, options).toTuple();
        if (error) {
            error.addContext(`When parsing component with key - '${componentKey}'`);
            return (0, error_values_1.err)(error);
        }
        base[componentKey] = componentSchema;
    }
    const schema = joi_1.default.object(base).unknown(true);
    return (0, error_values_1.ok)(schema);
}
//# sourceMappingURL=ValidateSchema.js.map